/**
 * Dialog de configuração do node de Mensagem Instagram
 * Permite configurar modo de resposta (público, privado, DM) e conteúdo
 * Padronizado com o mesmo layout do ConfigureMessageDialog
 * 
 * RELEASE: 1.1.0 - 2025-01-26 - Padronização de layout
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { FlowNode, InstagramReplyMode } from '@/types/flows';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { NodeHelpDialog } from './NodeHelpDialog';
import { TextareaWithVariables } from '../TextareaWithVariables';

interface ConfigureInstagramMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: any) => void;
  tenantId: string;
}

/**
 * Configurações dos modos de resposta
 */
const replyModeOptions: Array<{
  value: InstagramReplyMode;
  label: string;
  description: string;
  warning?: string;
}> = [
  {
    value: 'public_reply',
    label: 'Reply Público',
    description: 'Responde publicamente ao comentário (visível para todos no post)',
    warning: 'Só funciona quando o fluxo é ativado por um comentário',
  },
  {
    value: 'private_reply',
    label: 'Reply Privado',
    description: 'Envia DM ao autor do comentário (abre janela de 24h para conversa)',
    warning: 'Só funciona quando o fluxo é ativado por um comentário',
  },
  {
    value: 'direct_message',
    label: 'DM Direto',
    description: 'Mensagem direta padrão (requer janela de DM já aberta)',
    warning: 'Requer que o contato tenha iniciado uma conversa anteriormente',
  },
];

/**
 * Dialog de configuração do node de Mensagem Instagram
 */
export function ConfigureInstagramMessageDialog({
  open,
  onOpenChange,
  node,
  onSave,
  tenantId,
}: ConfigureInstagramMessageDialogProps) {
  // Estado do formulário
  const [replyMode, setReplyMode] = useState<InstagramReplyMode>('direct_message');
  const [text, setText] = useState('');
  /** Ref para capturar apelido do node */
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  const [showHelp, setShowHelp] = useState(false);

  // Sincroniza estado com node selecionado
  useEffect(() => {
    if (node && open) {
      const config = node.data.config || {};
      setReplyMode(config.reply_mode || 'direct_message');
      setText(config.text || '');
    }
  }, [node, open]);

  /**
   * Valida e salva a configuração
   */
  const handleSave = () => {
    // Validação: Reply Público e Privado requerem texto
    if ((replyMode === 'public_reply' || replyMode === 'private_reply') && !text.trim()) {
      toast.error('O texto da mensagem é obrigatório para este modo');
      return;
    }

    // Validação: pelo menos algo precisa ser configurado
    if (!text.trim()) {
      toast.error('Digite o conteúdo da mensagem');
      return;
    }

    // Validação: limite de caracteres
    if (text.length > 1000) {
      toast.error('O texto não pode exceder 1000 caracteres');
      return;
    }

    if (!node) return;

    onSave(node.id, {
      reply_mode: replyMode,
      text: text.trim(),
      use_variables: text.includes('{{'),
      nickname: nicknameRef.current?.getNickname(),
    });

    toast.success('Configuração salva!');
    onOpenChange(false);
  };

  // Encontra a configuração do modo selecionado
  const selectedModeConfig = replyModeOptions.find(opt => opt.value === replyMode);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header padronizado com botão de ajuda */}
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Configurar Mensagem Instagram</DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Campo ID do Node (somente leitura) */}
            <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

            <Separator className="my-2" />

            {/* Seleção do modo de resposta - layout padronizado */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Modo de Resposta</Label>
              
              <RadioGroup
                value={replyMode}
                onValueChange={(value) => setReplyMode(value as InstagramReplyMode)}
                className="space-y-2"
              >
                {replyModeOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer"
                    onClick={() => setReplyMode(option.value)}
                  >
                    <RadioGroupItem value={option.value} id={option.value} className="mt-0.5" />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor={option.value} className="font-medium cursor-pointer">
                        {option.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {option.description}
                      </p>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Alerta do modo selecionado */}
            {selectedModeConfig?.warning && (
              <Alert variant="default" className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
                  {selectedModeConfig.warning}
                </AlertDescription>
              </Alert>
            )}

            <Separator className="my-2" />

            {/* Campo de texto com variáveis - componente padronizado */}
            <div className="space-y-2">
              <Label>Texto da Mensagem *</Label>
              <TextareaWithVariables
                tenantId={tenantId}
                value={text}
                onChange={setText}
                placeholder="Digite o texto da mensagem... Use {{variavel}} para inserir variáveis"
                maxLength={1000}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {text.length}/1000 caracteres
              </p>
            </div>
          </div>

          {/* Footer padronizado */}
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de ajuda do node */}
      <NodeHelpDialog 
        open={showHelp} 
        onOpenChange={setShowHelp} 
        nodeType="instagram_message" 
      />
    </>
  );
}
